/**
 * SitePick front — Preview（`/sites/{slug}/live/`）。docs/07。
 *
 * 責務はこれだけ:
 *  - device toggle（PC / タブレット / スマホ。コンテナの state 変更のみ。幅は CSS）
 *  - iframe の遅延 src 設定（idle or 初回操作）
 *  - loading state の解除（iframe load）
 *  - timeout フォールバック（load が来ない場合の案内。iframe は消さない）
 *
 * favorites / compare store とは混ぜない。Preview 以外のページでは何もしない。
 */

import { viewPreview, previewError } from "./analytics.js";

/**
 * 実物デモにどこから入ったか（docs/17 §W.6）。
 * 着地 URL の `?sp_entry=` を見る。指定が無い経路は従来どおり商品詳細からなので "detail"。
 * 値は許可リストで固定する（GA4 のカスタムディメンションを任意文字列で汚さない）。
 */
const PREVIEW_ENTRIES = ["industry_fv", "card", "detail", "article", "share"];

function previewEntry() {
  try {
    const v = new URLSearchParams(window.location.search).get("sp_entry") || "";
    return PREVIEW_ENTRIES.includes(v) ? v : "detail";
  } catch {
    return "detail";
  }
}
import { promoteIframe } from "./iframe-frame.js";

const DEVICES = ["pc", "sp"];

/**
 * PC プレビューの基準幅。**デザイン時に想定している幅**と一致させる
 * （`capture-screenshots.mjs` の desktop-1440 / 各商品 qa.md の「1440 で確認」と同じ）。
 * CSS 側の `--sp-live-pc-width` と必ず揃えること。
 */
const PC_WIDTH = 1440;

/**
 * スマホ表示の viewport（2026-09-30 人間指示: ユーザー向けのプリセットは 390×844 の 1 種類）。
 * 端末より広いビューア（PC など）では iframe をこの大きさに固定し、入らなければ端末全体を scale で縮める。
 * CSS 側の `--sp-live-sp-width` / `--sp-live-sp-height` と必ず揃えること。
 */
const SP_WIDTH = 390;
const SP_HEIGHT = 844;

/**
 * 実スマホ相当のビューアか（CSS の SP ブレークポイントと同じ条件）。
 * このときスマホ表示は 390×844 を再現せず、端末の表示領域（100vw × 100dvh）をそのまま使う。
 *
 * @returns {boolean}
 */
function isPhoneViewer() {
  return Boolean(window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
}

/**
 * PC モードでステージ幅が基準幅未満のとき、基準幅の実レンダリングをステージ幅へ
 * transform: scale で縮小する（横スクロールを避ける）。属性は 1 つだけ触る。
 *
 * @param {HTMLElement|null} frame
 * @param {HTMLElement|null} stage
 */
function updateScale(frame, stage) {
  if (!frame || !stage) {
    return;
  }
  const isPc = frame.getAttribute("data-sp-preview-device") !== "sp";
  const width = stage.clientWidth;
  // スマホ表示（端末より広いビューア）: 390×844 の端末全体を、ステージに入る大きさまで外側から縮める。
  // iframe の大きさ（= デモの viewport）は変えない。実スマホでは全画面なので縮めない。
  if (!isPc && !isPhoneViewer()) {
    const gap = parseFloat(window.getComputedStyle(frame).top) || 0;
    const fit = Math.min(1, (stage.clientHeight - gap * 2) / SP_HEIGHT, (width - gap * 2) / SP_WIDTH);
    frame.style.setProperty("--sp-live-sp-scale", String(Math.max(0.1, fit)));
  } else {
    frame.style.removeProperty("--sp-live-sp-scale");
  }
  if (isPc && width > 0 && width < PC_WIDTH) {
    frame.style.setProperty("--sp-live-scale", String(width / PC_WIDTH));
    frame.classList.add("is-scaled");
    stage.classList.add("is-scaled");
  } else {
    frame.style.removeProperty("--sp-live-scale");
    frame.classList.remove("is-scaled");
    stage.classList.remove("is-scaled");
  }
}

/**
 * device 切替。
 *
 * @param {HTMLElement} frame
 * @param {HTMLElement|null} stage
 * @param {NodeListOf<HTMLElement>} buttons
 * @param {string} device
 * @param {string} itemId
 */
function setDevice(frame, stage, buttons, device, itemId, isLoaded) {
  const next = DEVICES.indexOf(device) === -1 ? "pc" : device;
  frame.setAttribute("data-sp-preview-device", next);
  // スマホ表示の状態（CSS: PC では 390×844 固定の枠 / 実スマホでは全画面 + 操作ボタン）。
  // 切り替えたら操作パネルは閉じる（全画面になった直後にデモを覆わない）。
  const live = frame.closest(".p-live");
  if (live) {
    live.classList.toggle("is-sp-device", next === "sp");
    setDock(live, false);
  }
  buttons.forEach((btn) => {
    const active = btn.getAttribute("data-sp-device") === next;
    btn.classList.toggle("is-active", active);
    btn.setAttribute("aria-selected", active ? "true" : "false");
  });
  updateScale(frame, stage);
  // デモが実際に表示されている間だけ「表示中の再エンゲージ」として数える。
  // 読み込み前・失敗中の切替は閲覧ではないので送らない。
  if (isLoaded()) {
    viewPreview(itemId, next, previewEntry());
  }
}

/**
 * 実スマホのスマホ表示で、操作 UI（上部バー・下部 CTA）をデモの上に重ねて出す / 隠す。
 *
 * @param {HTMLElement} live `.p-live`
 * @param {boolean} open
 */
function setDock(live, open) {
  live.classList.toggle("is-dock-open", open);
  const toggle = live.querySelector(".js-sp-live-dock");
  if (toggle && toggle.hasAttribute("data-sp-dock-compact")) {
    // コンパクトバー（parts/live.php の .p-live__mini）の「その他」。購入・相談・戻るはバーに常に出ているので、
    // ここで開くのは PC / スマホの切替・お気に入り・比較・共有など残りの操作だけ。
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.textContent = open ? "閉じる" : "その他";
    toggle.setAttribute("aria-label", open ? "その他の操作を閉じる" : "その他の操作（PC / スマホの切替・お気に入り・比較・共有）");
    return;
  }
  if (toggle) {
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    // 閉じているときは「誰のボタンか（SITE PICK）」と「何ができるか（購入・戻る）」を 2 段で出す
    // （デモサイト側のボタンと取り違えない・購入導線を見失わない。2026-09-30 人間判断）。
    toggle.textContent = "";
    if (open) {
      toggle.textContent = "閉じる";
    } else {
      const brand = document.createElement("span");
      brand.className = "p-live__dock-brand";
      brand.textContent = "SITE PICK";
      const label = document.createElement("span");
      label.className = "p-live__dock-label";
      label.textContent = "申込み・戻る";
      toggle.append(brand, label);
    }
    toggle.setAttribute("aria-label", open ? "SITE PICK の操作を閉じる" : "SITE PICK の操作を開く（申し込む・戻る・PC / スマホの切替）");
  }
}

/**
 * 実スマホのスマホ表示の「操作」ボタンを作る（端末切替自体が JS 前提なので、ボタンも JS が用意する）。
 * 表示の条件は CSS（`.p-live.is-sp-device` かつ SP 幅）。
 *
 * @param {HTMLElement} live
 * @param {NodeListOf<HTMLElement>} buttons 端末切替ボタン（全画面へ切り替えた直後にフォーカスを移す先を決める）
 */
function initDock(live, buttons) {
  // テンプレートにコンパクトバーの「その他」があればそれを使う。無ければ従来の浮動ボタンを作る。
  let toggle = live.querySelector(".js-sp-live-dock");
  if (!toggle) {
    toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "p-live__dock-toggle js-sp-live-dock";
    live.appendChild(toggle);
  }
  setDock(live, false);
  toggle.addEventListener("click", () => {
    setDock(live, !live.classList.contains("is-dock-open"));
  });
  live.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && live.classList.contains("is-dock-open")) {
      setDock(live, false);
      toggle.focus();
    }
  });
  // 実スマホで「スマホ」を押すと押したボタン（上部バー）が隠れるので、フォーカスを「操作」へ移す。
  buttons.forEach((btn) => {
    btn.addEventListener("click", () => {
      if (live.classList.contains("is-sp-device") && isPhoneViewer()) {
        toggle.focus();
      }
    });
  });
}

/**
 * 初期化。`[data-sp-preview]` が無ければ何もしない。
 *
 * @returns {void}
 */
export function initPreview() {
  const root = document.querySelector("[data-sp-preview]");
  if (!root) {
    return;
  }

  let itemId = "";
  try {
    const parsed = JSON.parse(root.getAttribute("data-sp-item") || "{}");
    itemId = parsed && parsed.item_id ? String(parsed.item_id) : "";
  } catch {
    itemId = "";
  }

  const frame = root.querySelector(".p-live__frame");
  const stage = root.querySelector(".p-live__stage");
  const iframe = root.querySelector(".p-live__iframe");
  const loading = root.querySelector("[data-sp-preview-loading]");
  const fallback = root.querySelector("[data-sp-preview-fallback]");
  const buttons = root.querySelectorAll(".js-sp-device");

  // デモが実際に表示されたか（iframe の load 成功）。計測の前提条件。
  let loaded = false;
  const isLoaded = () => loaded;

  // device toggle（キーボードでも操作可能。button 要素なので Enter/Space は既定で動く）。
  if (frame && buttons.length) {
    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        setDevice(frame, stage, buttons, btn.getAttribute("data-sp-device") || "pc", itemId, isLoaded);
      });
    });
  }

  if (frame && buttons.length && root.classList.contains("p-live")) {
    initDock(root, buttons);
  }

  // 実スマホ（SP 幅）で開いたら初期表示は「スマホ」（2026-10-05 人間承認）。PC 表示の 1440×900 を端末幅へ縮小すると
  // 画面の 3 割ほどの帯になり文字が読めない。操作 UI は開かない（デモの表示領域を優先）。購入・相談・戻るは
  // 下端のコンパクトバー（.p-live__mini）に常に出る。`?device=pc` なら従来どおり PC。読み込み前なので計測は送らない。
  const requested = new URLSearchParams(window.location.search).get("device");
  if (frame && buttons.length && requested !== "pc" && isPhoneViewer()) {
    setDevice(frame, stage, buttons, "sp", itemId, () => false);
  }

  // PC モードの scale 縮小: 初期 + リサイズ / 向き変更で追従（rAF スロットル）。
  updateScale(frame, stage);
  let scaleRaf = 0;
  const onResize = () => {
    window.cancelAnimationFrame(scaleRaf);
    scaleRaf = window.requestAnimationFrame(() => updateScale(frame, stage));
  };
  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("orientationchange", onResize, { passive: true });

  if (!(iframe instanceof HTMLIFrameElement)) {
    return;
  }

  // 遅延ロード: idle か初回操作で src を昇格。長時間空白にはしない。
  //
  // 計測は **実ロードが成功したときだけ**（`onLoad`）。src を入れた時点で送ると
  // 読み込めなかった閲覧まで「デモを見た」と数えてしまう
  // （growth/ssot/measurement.yaml の semantics_fix）。
  let promoted = false;
  const device = () => (frame ? frame.getAttribute("data-sp-preview-device") || "pc" : "pc");
  const go = () => {
    if (promoted) {
      return;
    }
    promoted = true;
    promoteIframe(iframe, {
      loading,
      fallback,
      onLoad: () => {
        loaded = true;
        viewPreview(itemId, device(), previewEntry());
      },
      onTimeout: () => previewError(itemId, "timeout"),
    });
  };

  const kick = () => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(go, { timeout: 1500 });
    } else {
      window.setTimeout(go, 400);
    }
  };

  // 早すぎず遅すぎず: 次フレームで idle 予約 + 念のため上限。
  window.requestAnimationFrame(kick);
  window.setTimeout(go, 2500);

  // ユーザーが先に触ったら即ロード。
  ["pointerdown", "keydown", "touchstart"].forEach((evt) => {
    root.addEventListener(evt, go, { once: true, passive: true });
  });
}
